import type React from 'react';

/**
 * Switch - Componente padrão de toggle switch do sistema Joshua
 *
 * Características:
 * - Cor azul Joshua (var(--color-primary) = #154680) quando ativo
 * - Animação suave de transição
 * - Labels opcionais (Sim/Não, Ativo/Inativo, etc.)
 * - Suporte a disabled
 * - Usa variáveis CSS do styles/globals.css (--color-primary, --color-primary-hover, etc.)
 *
 * @example - Uso básico
 * <Switch
 *   checked={formData.ativo}
 *   onChange={(value) => handleFieldChange('ativo', value)}
 * />
 *
 * @example - Com labels customizados
 * <Switch
 *   checked={formData.pipeline_show}
 *   onChange={(value) => handleFieldChange('pipeline_show', value)}
 *   activeLabel="Sim"
 *   inactiveLabel="Não"
 * />
 *
 * @example - Sem label
 * <Switch
 *   checked={formData.ativo}
 *   onChange={(value) => handleFieldChange('ativo', value)}
 *   showLabel={false}
 * />
 */
interface SwitchProps {
  checked?: boolean;
  onChange?: (value: boolean) => void;
  activeLabel?: string;
  inactiveLabel?: string;
  showLabel?: boolean;
  disabled?: boolean;
  size?: 'small' | 'default' | 'large';
}

export default function Switch({
  checked = false,
  onChange,
  activeLabel = 'Sim',
  inactiveLabel = 'Não',
  showLabel = true,
  disabled = false,
  size = 'default'
}: SwitchProps) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  // Dimensões baseadas no tamanho
  const sizes = {
    small: { track: { width: 36, height: 20 }, thumb: 14, gap: 6 },
    default: { track: { width: 44, height: 24 }, thumb: 18, gap: 8 },
    large: { track: { width: 52, height: 28 }, thumb: 22, gap: 10 }
  };

  const s = sizes[size] || sizes.default;
  const thumbOffset = checked ? s.track.width - s.thumb - 4 : 2;

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        opacity: disabled ? 0.5 : 1
      }}
    >
      {/* Track (trilha do switch) */}
      <div
        style={{
          width: `${s.track.width}px`,
          height: `${s.track.height}px`,
          backgroundColor: checked ? 'var(--color-primary, #154680)' : '#e5e7eb',
          borderRadius: `${s.track.height / 2}px`,
          position: 'relative',
          transition: 'background-color 0.2s ease',
          border: `2px solid ${checked ? 'var(--color-primary-hover, #0f3460)' : '#d1d5db'}`,
          boxShadow: checked ? '0 0 0 2px var(--color-primary-shadow, rgba(21, 70, 128, 0.1))' : 'none'
        }}
      >
        {/* Thumb (bolinha do switch) */}
        <div
          style={{
            width: `${s.thumb}px`,
            height: `${s.thumb}px`,
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '1px',
            left: `${thumbOffset}px`,
            transition: 'left 0.2s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        />
      </div>

      {/* Label */}
      {showLabel && (
        <span
          style={{
            marginLeft: `${s.gap}px`,
            fontSize: size === 'small' ? '12px' : '13px',
            fontWeight: 500,
            color: checked ? 'var(--color-primary, #154680)' : 'var(--color-muted, #6b7280)',
            transition: 'color 0.2s ease'
          }}
        >
          {checked ? activeLabel : inactiveLabel}
        </span>
      )}
    </div>
  );
}
